import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { ImagePreviewComponent } from '@app/shared/components';
import { UploadedFileModel } from '@app/shared/components/file-upload/models/uploaded-file.model';
import { isNil } from '@app/utils';

@Component({
	selector: 'formly-image-preview',
	template: `
		<image-preview
			[readOnly]="props?.isReadOnly === true || props?.disabled === true"
			[heightProp]="heightProp"
			[labelText]="props.labelText"
			[noDataMessage]="props.noDataMessage || noDataMessage"
			[showAllowedExtensions]="showAllowedExtensions"
			[bindToUrl]="bindToUrl"
			[imageUrl]="props.bindToUrl ? displayImage : null"
			[uploadedFile]="props.bindToUrl ? null : displayImage"
			(imageChangeEmitter)="onImageChange($event)"
		></image-preview>
	`,
	standalone: true,
	imports: [ImagePreviewComponent],
})

/** Image Preview component to select, save, and display images
 *
 * Custom Template Options:
 * NOTE: Don't try to use props.readonly or props.readOnly here. Formly appears to always add those props
 * automatically, they were defaulting to true, and they couldn't be overridden in our code.
 * @prop  disabled: boolean (optional) - The disabled / readonly state of the image control.
 * @prop  isReadOnly: boolean (optional) - The disabled / readonly state of the image control.
 * @prop  heightProp: number (optional) - The height of the image preview. Default is 170.
 * @prop  labelText: string (optional) - Text to be displayed as a header label above the image.
 * @prop  noDataMessage: string (optional) - Text to be displayed in the component when there is no image selected.
 * @prop  bindToUrl: boolean (optional) - Indicates whether the component should bind to a URL for the image (such as
 * a Vault location). The default is to bind to the binary for the file itself.
 * @prop  initialUrl: string (optional) - Used to set the initial image URL. This should only be used when the bindToUrl
 * prop is true, and is ignored if the bindToUrl prop is false. NOTE: If the key is set, binding this control to a model,
 * this prop will override the value in the model for the initial image.
 * @prop  initialFile: UploadedFileModel | string (optional) - Used to set the initial image file. This should only be used when
 * the bindToUrl prop is false, and is ignored if the bindToUrl prop is true. A string is expected to be a base64 encoded binary
 * of the file. NOTE: If the key is set, binding this control to a model, this prop will override the value in the model for the
 * initial image.
 * @prop  showAllowedExtensions: boolean (optional) - Indicates whether the control should display the file extensions
 * the user is allowed to select. The default is true.
 * @prop  onImageChange: (fileModel?: UploadedFileModel, field?: FormlyFieldConfig) => void (optional) - Callback function
 * that is called when an image is selected or changed.
 */
export class FormlyImagePreviewComponent extends FieldType<FormlyFieldConfig> implements OnInit {
	heightProp = 170;
	noDataMessage = 'No image available';
	bindToUrl = false;
	showAllowedExtensions = true;
	protected displayImage: string = null;

	private loadingDisplayImage = false;
	private updatingFromModel = false;
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		if (!isNaN(this.props.heightProp) && this.props.heightProp > 0) {
			this.heightProp = this.props.heightProp;
		}

		this.showAllowedExtensions = this.props.showAllowedExtensions ?? true;

		this.bindToUrl = this.props.bindToUrl ?? false;

		this.setInitialImage();
		this.setupValueChangeListener();
	}

	// This method is called whenever the imageChangeEmitter event is fired from the ImagePreviewComponent,
	// which happens when a user selects a new image or removes the existing image. It updates the form model
	// with the new image data and calls the onImageChange callback if it is provided in the props.
	onImageChange(fileModel?: UploadedFileModel): void {
		// The image change emitter fires when programmatically setting the display value, so we need to
		// ignore that to prevent false or duplicate change events.
		if (this.loadingDisplayImage) {
			this.loadingDisplayImage = false;
			return;
		}

		this.updateFormModel(fileModel);

		// Keep displayImage in sync with what the user has selected so that
		// a subsequent viewForm.reset() is detected as an actual change.
		const newDisplayValue = (this.bindToUrl ? fileModel?.url : fileModel?.fileBase64Encoded) ?? null;
		if (newDisplayValue !== this.displayImage) {
			this.loadingDisplayImage = true;
			this.displayImage = newDisplayValue;
		}

		if (this.props?.onImageChange) {
			this.props.onImageChange(fileModel, this.field);
		}
	}

	// This method determines the initial image to be displayed in the control based on the provided props and model value.
	// The priority is as follows:
	// 1. If bindToUrl is true and props.initialUrl is provided, use props.initialUrl
	// 2. If bindToUrl is false and props.initialFile is a string, use props.initialFile
	// 3. If bindToUrl is false and props.initialFile.fileBase64Encoded is a string, use props.initialFile.fileBase64Encoded
	// 4. If the model value (this.value, the Formly model binding) is a string, use this.value
	private setInitialImage(): void {
		if (this.bindToUrl && typeof this.props.initialUrl === 'string' && this.props.initialUrl !== '') {
			this.displayImage = this.props.initialUrl;
		} else if (!this.bindToUrl && typeof this.props.initialFile === 'string' && this.props.initialFile !== '') {
			this.displayImage = this.props.initialFile;
		} else if (!this.bindToUrl
			&& typeof this.props.initialFile?.fileBase64Encoded === 'string'
			&& this.props.initialFile?.fileBase64Encoded !== ''
		) {
			this.displayImage = this.props.initialFile.fileBase64Encoded;
		} else if (typeof this.value === 'string' && this.value !== '') {
			this.displayImage = this.value;
		}

		this.loadingDisplayImage = !isNil(this.displayImage);
	}

	private setupValueChangeListener(): void {
		// Listen for changes to the form control's value. This is necessary to update the displayed image
		// if the form model is changed programmatically from outside this component. For example, if the user
		// clicks a "Cancel" button that resets the form, we need to update the displayed image to match the
		// reset model value.
		this.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newValue) => {
			if (this.updatingFromModel) {
				return;
			}
			const updatedImage = typeof newValue === 'string' && newValue !== '' ? newValue : null;
			if (updatedImage === this.displayImage) {
				return;
			}
			this.loadingDisplayImage = true;
			this.displayImage = updatedImage;
		});
	}

	private updateFormModel(fileModel?: UploadedFileModel): void {
		// Check for a model binding before trying to update the form control value.
		if (typeof this.key !== 'string' || this.key === '') {
			return;
		}

		const currentValue = this.formControl.value;
		const newValue = (this.bindToUrl ? fileModel?.url : fileModel?.fileBase64Encoded) ?? null;

		if ((this.isEmpty(currentValue) && this.isEmpty(newValue)) || currentValue === newValue) {
			return;
		}

		// markAsDirty/markAsTouched must come before setValue so the form is already
		// dirty when valueChanges fires synchronously during setValue.
		this.formControl.markAsDirty();
		this.formControl.markAsTouched();
		this.updatingFromModel = true;
		this.formControl.setValue(newValue);
		this.updatingFromModel = false;
	}

	private isEmpty(value: any): boolean {
		return isNil(value) || (typeof value === 'string' && value.trim() === '');
	}
}
